import { ChangeEvent, useCallback, useEffect, useMemo, useState } from "react";
import { Box, Input as MuiInput, Typography } from "@mui/material";
import { useNavigate } from "react-router-dom";
import PowerSettingsNewIcon from "@mui/icons-material/PowerSettingsNew";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";

import { ReactComponent as SearchIcon } from "../../assets/svg/Search.svg";
import style from "./courier.module.css";
import { Button, LabelBox } from "../../components";
import { colors } from "../../theme/colors";
import { RootState, useAppSelector } from "../../store";
import { CustomTable, StatusBadge } from "../../components/DataTable";
import { ReactComponent as DeactivateIcon } from "../../assets/svg/deactivate.svg";
import { ReactComponent as EditIcon } from "../../assets/svg/edit.svg";
import { ReactComponent as EyeIcon } from "../../assets/svg/eye.svg";
import { ActionItem, CustomActionMenu } from "../../components/Menu";
import Logs from "../../assets/images/logs.png";
import {
  useGetUserRolesQuery,
  useGetUsersListQuery,
  useUpdateUserMutation,
} from "../../services/api";
import {
  ConfirmationDialog,
  DetailsDialog,
  InputDialog,
} from "../../components/Dialog";
import { Input } from "../../components/UserInput";
import { updateStatusSchema } from "../../yup";
import { getErrorMessage } from "../../utils/common";
import { formatCourierName } from "./utils";

type FormState = typeof updateStatusSchema.__outputType;
type CourierAction = "view" | "edit" | "logs" | "deactivate" | "activate";

const DEFAULT_COURIER_ROLE_ID = 5;

type UserRoleItem = {
  id: number;
  name: string;
};

type UserRolesResponse = {
  items?: UserRoleItem[];
};

type UsersListResponse = {
  items?: Users[];
  count?: number;
};

/**
 * Renders a table cell with clamped text.
 *
 * Args:
 *   text: Text content to render in the table cell.
 *
 * Returns:
 *   A Typography element configured for ellipsis truncation.
 */
const renderCellText = (text: string) => (
  <Typography
    className={style.cell}
    sx={{
      textOverflow: "ellipsis",
      display: "-webkit-box",
      WebkitLineClamp: "2",
      WebkitBoxOrient: "vertical",
      overflow: "hidden",
    }}
  >
    {text}
  </Typography>
);

/**
 * Builds the action menu list for a courier row.
 *
 * Args:
 *   isActive: Whether the courier is currently active.
 *
 * Returns:
 *   An ordered list of menu actions for the row.
 */
const getCourierActions = (isActive: boolean): ActionItem<CourierAction>[] => [
  {
    label: "View",
    value: "view",
    icon: <EyeIcon height={24} width={24} />,
  },
  ...(isActive
    ? ([
      {
        label: "Edit",
        value: "edit",
        icon: <EditIcon height={24} width={24} />,
      },
    ] as ActionItem<CourierAction>[])
    : []),
  {
    label: "Logs",
    value: "logs",
    icon: <img src={Logs} alt="Logs" width={24} />,
  },
  {
    label: isActive ? "Deactivate" : "Activate",
    value: isActive ? "deactivate" : "activate",
    icon: isActive ? (
      <DeactivateIcon height={24} width={24} />
    ) : (
      <PowerSettingsNewIcon height={24} width={24} />
    ),
  },
];

/**
 * Displays the courier list with search, status actions, and navigation.
 *
 * Returns:
 *   The courier list page component.
 */
const Courier = () => {
  const { userDetails } = useAppSelector(
    (state: RootState) => state.auth
  );
  const navigate = useNavigate();
  const [updateUser] = useUpdateUserMutation();

  const [message, setMessage] = useState<LabelMessageType | null>(null);
  const [selectedCourier, setSelectedCourier] = useState<Users | null>(null);
  const [modalVisible, setModalVisible] = useState(false);
  const [reasonModalVisible, setReasonModalVisible] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [logModalVisible, setLogModalVisible] = useState(false);

  const { data: rolesData } = useGetUserRolesQuery({
    is_active: true,
    is_detailed: true,
  }) as { data?: UserRolesResponse };

  const courierRoleId = useMemo(
    () =>
      rolesData?.items?.find(
        (role) => role?.name === "Courier"
      )?.id,
    [rolesData]
  );

  // all query parameters and pagination params
  const [queryParam, setQueryParam] = useState({
    search_key: "",
    order_by_columns: "surname",
    order: "Asc",
    is_detailed: true,
    laboratory_id: userDetails?.laboratory_id ?? 0,
    user_role_id: courierRoleId ?? DEFAULT_COURIER_ROLE_ID,
  });

  useEffect(() => {
    // Keep role-based filters aligned once roles load to avoid stale defaults.
    if (courierRoleId && queryParam.user_role_id !== courierRoleId) {
      setQueryParam((prevParams) => ({
        ...prevParams,
        user_role_id: courierRoleId,
      }));
    }
  }, [courierRoleId, queryParam.user_role_id]);

  useEffect(() => {
    const laboratoryId = userDetails?.laboratory_id;
    if (
      typeof laboratoryId === "number" &&
      queryParam.laboratory_id !== laboratoryId
    ) {
      setQueryParam((prevParams) => ({
        ...prevParams,
        laboratory_id: laboratoryId,
      }));
    }
  }, [queryParam.laboratory_id, userDetails?.laboratory_id]);

  /**
   * Handles action menu selections for a courier row.
   *
   * Args:
   *   value: Selected action value.
   *   courier: The courier row associated with the action.
   */
  const handleMenu = useCallback(
    (value: CourierAction, courier: Users) => {
      setSelectedCourier(courier);
      if (value === "deactivate") {
        setModalVisible(true);
      } else if (value === "activate") {
        setModalVisible(true);
      } else if (value === "view") {
        navigate(`/couriers/details/${courier?.id}`);
      } else if (value === "edit") {
        navigate(`/couriers/edit/${courier?.id}`);
      } else if (value === "logs") {
        setLogModalVisible(true);
      }
    },
    [navigate]
  );

  const fields = useMemo(
    () => [
      {
        id: "surname",
        label: "Name",
        flex: 2.5,
        sortable: true,
        type: "name",
        render: (item: Users) =>
          renderCellText(
            formatCourierName(item)
          ),
      },
      {
        id: "mail",
        label: "Email Address",
        flex: 2.5,
        type: "name",
        render: (item: Users) => renderCellText(item?.mail ?? ""),
      },
      {
        id: "is_active",
        label: "Status",
        type: "badge",
        flex: 1.5,
        sortable: true,
        render: (item: Users) => (
          <StatusBadge isActive={item.is_active ?? false} />
        ),
      },
      {
        id: "logo_s3_key_name",
        label: "Actions",
        type: "action",
        render: (item: Users) => (
          <CustomActionMenu
            item={item}
            isActive={item?.is_active ?? false}
            handleChange={handleMenu}
            actions={getCourierActions(item.is_active ?? false)}
          />
        ),
      },
    ],
    [handleMenu]
  );

  const {
    data: courierData,
    refetch: refetchCouriers,
    isFetching,
  } = useGetUsersListQuery(queryParam) as {
    data?: UsersListResponse;
    refetch: () => Promise<unknown>;
    isFetching: boolean;
  };

  // for searching in the query
  const handleSearch = useCallback((event: ChangeEvent<HTMLInputElement>) => {
    const { value } = event.target;
    setQueryParam((prevParams) => ({
      ...prevParams,
      search_key: value,
    }));
  }, []);

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
  } = useForm<FormState>({
    resolver: yupResolver(updateStatusSchema),
  });

  /**
   * Submits a courier status update, optionally including a deactivate reason.
   *
   * Args:
   *   values: Form values containing the optional deactivate reason.
   *
   * Returns:
   *   A promise that resolves when the update completes.
   */
  const onSubmit = async (values: FormState) => {
    // Avoid status updates when no row is selected to prevent bad payloads.
    if (!selectedCourier?.id) {
      setMessage({
        type: "error",
        text: "Please select a valid courier before updating status.",
      });
      return;
    }
    try {
      setIsLoading(true);
      const { deactivate_reason } = values;
      await updateUser({
        id: selectedCourier.id,
        is_active: !selectedCourier?.is_active,
        deactivate_reason: deactivate_reason ?? "",
      }).unwrap();
      setMessage({
        type: "success",
        text: "Status updated successfully.",
      });
      setReasonModalVisible(false);
      setValue("deactivate_reason", "");
      clearErrors();
      await refetchCouriers();
    } catch (error: unknown) {
      setMessage({
        type: "error",
        text: getErrorMessage(error, "Something went wrong!"),
      });
    } finally {
      setIsLoading(false);
    }
  };

  // Render the Courier component
  return (
    <Box>
      <Box
        sx={{
          display: "flex",
          justifyContent: "space-between",
          marginTop: "15px",
        }}
      >
        <MuiInput
          disableUnderline
          placeholder="Search by name...."
          type="search"
          startAdornment={<SearchIcon width={20} height={20} />}
          value={queryParam.search_key}
          onChange={handleSearch}
          sx={{ width: "550px", gap: "8px", borderRadius: "50px", backgroundColor: colors.white }}
        />
        <Button
          variant="contained"
          className={style.addButton}
          label={"Add New Courier"}
          sx={{
            ":hover": { backgroundColor: colors.primary },
          }}
          disabled={!courierRoleId}
          onClick={() => {
            if (!courierRoleId) {
              setMessage({
                type: "error",
                text: "User role is not available yet. Please wait for roles to load.",
              });
              return;
            }
            navigate("add", { state: { userRole: courierRoleId } });
          }}
        />
      </Box>
      {message && (
        <Box mt={2}>
          <LabelBox
            boxProps={{ height: 50, display: "flex", alignItems: "center" }}
            data={message}
            onClose={() => setMessage(null)}
          />
        </Box>
      )}
      <Box style={{ maxHeight: "calc(100vh - 195px)", overflow: "auto" }}>
        <CustomTable<Users>
          fields={fields}
          paginationRequired={true}
          data={courierData?.items ?? []}
          loading={isFetching}
          onDoubleClick={(courier: Users) => {
            navigate(`/couriers/details/${courier?.id}`);
          }}
          defaultOrderBy={"surname"}
          searchTerm={queryParam.search_key}
          onClearSearch={() => setQueryParam((prev) => ({ ...prev, search_key: "" }))}
        />
      </Box>
      {modalVisible && (
        <ConfirmationDialog
          visible={modalVisible}
          message={`Are you sure you want to ${selectedCourier?.is_active ? "deactivate " : "activate "
            }"${selectedCourier?.surname?.toUpperCase() ?? ""}, ${selectedCourier?.given_name ?? ""
            }"?`}
          onClose={() => {
            setModalVisible(false);
          }}
          onConfirm={() => {
            setModalVisible(false);
            // Deactivation requires a reason; activation does not.
            if (selectedCourier?.is_active) {
              setReasonModalVisible(true);
            } else {
              onSubmit({ deactivate_reason: "" });
            }
          }}
        />
      )}
      {reasonModalVisible && (
        <InputDialog
          visible={reasonModalVisible}
          onClose={() => {
            setReasonModalVisible(false);
            setValue("deactivate_reason", "");
            clearErrors();
          }}
        >
          <Box
            component="form"
            sx={{ marginX: "20px" }}
            onSubmit={handleSubmit(onSubmit)}
          >
            <Typography
              variant="h2"
              component="div"
              fontSize={16}
              fontWeight={700}
              color={colors.modalHeading}
              marginBottom={"10px"}
              sx={{ display: "flex", flexDirection: "row" }}
            >
              {"Enter the reason for deactivation"}
              <Typography
                color={"red"}
                fontSize={16}
                fontWeight={700}
                sx={{ marginLeft: "5px", marginTop: "-5px" }}
              >
                {"*"}
              </Typography>
            </Typography>
            <Input
              placeholder=""
              name="deactivate_reason"
              required
              register={register}
              multiline
              rows={3}
              errors={errors}
              sx={{
                width: "100%",
                margin: "1px",
                borderWidth: "1px",
              }}
            />
            <Box className={style.horizontalButtons} sx={{ paddingX: "50px" }}>
              <Button
                sx={{
                  width: "150px",
                  height: "35px",
                  backgroundColor: colors.white,
                  ":hover": { backgroundColor: colors.white },
                }}
                variant="outlined"
                onClick={() => {
                  setReasonModalVisible(false);
                  setValue("deactivate_reason", "");
                  clearErrors();
                }}
                label={"Cancel"}
              />
              <Button
                sx={{
                  width: "150px",
                  height: "35px",
                  ":hover": { backgroundColor: colors.primary },
                }}
                variant="contained"
                type="submit"
                label={"Submit"}
                loading={isLoading}
              />
            </Box>
          </Box>
        </InputDialog>
      )}
      {logModalVisible && (
        <DetailsDialog
          visible={logModalVisible}
          details={selectedCourier}
          onClose={() => {
            setLogModalVisible(false);
          }}
          onConfirm={() => {
            setLogModalVisible(false);
          }}
        />
      )}
    </Box>
  );
};
export default Courier;
